import { type ComponentTFunction } from '@pkgs/components/providers/AppContextProvider';
import { BusinessArea, type PropertySubType, type SearchFilterRequestPropertyTypeSubType } from '@ev-tech/search-modules-api';

import { getPropertySubTypeKey, getPropertyTypeKey } from './translationKeyUtils';
import { PROPERTY_TYPE_SELECTION_KINDS } from './aiSearchKinds';
import { getSelectedPropertyTypeSubType } from '../hooks/stores/search/slices/objectTypeFilterSlice';
import { type SearchState } from '../hooks/stores/search/SearchStoreProvider';

type PropertyTypeState = SearchState['propertyTypeState'];
type ObjectTypeCategory = SearchState['objectTypeCategory'];

export type PropertyTypeSelectionEntry =
  | {
      kind: typeof PROPERTY_TYPE_SELECTION_KINDS.objectType;
      label: string;
      value: ObjectTypeCategory;
    }
  | {
      kind: typeof PROPERTY_TYPE_SELECTION_KINDS.propertyType;
      label: string;
      propertyType: keyof SearchFilterRequestPropertyTypeSubType;
    }
  | {
      kind: typeof PROPERTY_TYPE_SELECTION_KINDS.propertySubType;
      label: string;
      propertyType: keyof SearchFilterRequestPropertyTypeSubType;
      propertySubType: PropertySubType;
    };

export const getObjectTypeCategoryLabel = (objectTypeCategory: ObjectTypeCategory, t: ComponentTFunction): string => {
  if (objectTypeCategory === BusinessArea.residential) {
    return t('search.searchbar.advFilter.propertyType.residential');
  }

  if (objectTypeCategory === BusinessArea.commercial) {
    return t('search.searchbar.advFilter.propertyType.commercial');
  }

  const objectTypeTranslationKey = getPropertyTypeKey(objectTypeCategory);
  return objectTypeTranslationKey ? t(objectTypeTranslationKey) : objectTypeCategory;
};

export const getPropertyTypeLabel = (propertyType: keyof SearchFilterRequestPropertyTypeSubType, t: ComponentTFunction): string => {
  const propertyTypeTranslationKey = getPropertyTypeKey(propertyType);
  return propertyTypeTranslationKey ? t(propertyTypeTranslationKey) : propertyType;
};

export const getPropertySubTypeLabel = (propertySubType: PropertySubType, t: ComponentTFunction): string => {
  const propertySubTypeTranslationKey = getPropertySubTypeKey(propertySubType);
  return propertySubTypeTranslationKey ? t(propertySubTypeTranslationKey) : propertySubType;
};

export const getPropertyTypeSelectionEntries = (
  propertyTypeState: PropertyTypeState | undefined,
  objectTypeCategory: ObjectTypeCategory,
  t: ComponentTFunction,
): PropertyTypeSelectionEntry[] => {
  const propertyTypeSubType = getSelectedPropertyTypeSubType(propertyTypeState, objectTypeCategory);

  if (Object.keys(propertyTypeSubType).length === 0) {
    return [
      {
        kind: PROPERTY_TYPE_SELECTION_KINDS.objectType,
        label: getObjectTypeCategoryLabel(objectTypeCategory, t),
        value: objectTypeCategory,
      },
    ];
  }

  return Object.entries(propertyTypeSubType).flatMap<PropertyTypeSelectionEntry>(([propertyType, subTypes]) => {
    const typedPropertyType = propertyType as keyof SearchFilterRequestPropertyTypeSubType;

    if (subTypes.length === 0) {
      return [
        {
          kind: PROPERTY_TYPE_SELECTION_KINDS.propertyType,
          label: getPropertyTypeLabel(typedPropertyType, t),
          propertyType: typedPropertyType,
        } satisfies PropertyTypeSelectionEntry,
      ];
    }

    return subTypes.map(
      (propertySubType) =>
        ({
          kind: PROPERTY_TYPE_SELECTION_KINDS.propertySubType,
          label: getPropertySubTypeLabel(propertySubType, t),
          propertyType: typedPropertyType,
          propertySubType,
        }) satisfies PropertyTypeSelectionEntry,
    );
  });
};

export const getPropertyTypeSelectionLabels = (
  propertyTypeState: PropertyTypeState | undefined,
  objectTypeCategory: ObjectTypeCategory,
  t: ComponentTFunction,
): string[] => getPropertyTypeSelectionEntries(propertyTypeState, objectTypeCategory, t).map(({ label }) => label);
